import { useEffect } from "react";
import Content from "../Layout/Content";
import { Box } from "@mui/material";
import BookingFlow from "./components/BookingFlow";
import Summary from "./components/Summary";
import { Typography, useConfirmDialog } from "@powerdiary/components-library/lib";
import {
    useClientPortalSelector,
    useClientPortalDispatch,
    selectBooking,
    initializeBooking,
    setClientPortalGuid,
    clearBookingState,
    setCancelModalOpen,
} from "../../redux";
import { useClientPortalConfigContext } from "../../context";
import styles from "./AppointmentBooking.module.css";
import { ROUTES } from "../../routes";
import { useNavigate } from "react-router";
import { useAnalytics } from "hooks/useAnalytics";
import { BookingType } from "types/appointmentBooking";
import { useBookingNavigation } from "./hooks/useBookingNavigation";
import { BookingStatus, STEP_CONFIRMATION } from "./bookingSlice.types";
import {
    getBookAGroupBookingLabel,
    getBookGroupBookingLabel,
    getConfirmedGroupBookingLabel,
} from "../../utils/groupBookingLabel";

const getBookingStatusHeading = (
    bookingStatus: BookingStatus,
    bookingType: BookingType,
    groupBookingLabel?: string | null
): string => {
    const typeText = bookingType === BookingType.CLASS ? "class" : "appointment";

    switch (bookingStatus) {
        case BookingStatus.IN_PROGRESS:
            return bookingType === BookingType.CLASS
                ? getBookAGroupBookingLabel(groupBookingLabel)
                : `Book an ${typeText}`;
        case BookingStatus.BOOKED:
            return bookingType === BookingType.CLASS
                ? getConfirmedGroupBookingLabel(groupBookingLabel)
                : `Your ${typeText} booking is confirmed`;
        default:
            return bookingType === BookingType.CLASS
                ? getBookAGroupBookingLabel(groupBookingLabel)
                : `Book an ${typeText}`;
    }
};

interface AppointmentBookingProps {
    bookingType: BookingType;
}

export default function AppointmentBooking({ bookingType }: Readonly<AppointmentBookingProps>) {
    const dispatch = useClientPortalDispatch();
    const { clientPortalGuid, clientPortalConfig } = useClientPortalConfigContext();
    const { navigation, session, location, service, provider, availability } = useClientPortalSelector(selectBooking);
    const { effectiveCurrentStep } = useBookingNavigation();
    const shouldShowSummary = effectiveCurrentStep !== STEP_CONFIRMATION;
    const navigate = useNavigate();
    const { track } = useAnalytics();
    const groupBookingLabel = clientPortalConfig?.groupBookingLabel;

    useEffect(() => {
        track("page_view", {
            page_title:
                bookingType === BookingType.CLASS ? getBookGroupBookingLabel(groupBookingLabel) : "Book Appointment",
            booking_type: bookingType,
        });
    }, [bookingType, groupBookingLabel, track]);

    const { confirm } = useConfirmDialog({
        title: "Confirmation",
        content: "Are you sure you want to cancel?",
        onConfirm: () => {
            track("booking_cancelled", {
                booking_type: session.bookingType,
                current_step: navigation.currentStep,
                had_location: !!location.data,
                had_service: !!service.data,
                had_provider: !!provider.data,
                had_availability: !!availability.data,
            });
            dispatch(clearBookingState());
            dispatch(setCancelModalOpen(false));
            void navigate(`/clientportal/${clientPortalGuid}/${ROUTES.HOME.urlPath}`);
        },
        onCancel: () => {
            dispatch(setCancelModalOpen(false));
        },
        confirmationText: "Yes",
        cancellationText: "No",
    });

    const headingText = getBookingStatusHeading(session.bookingStatus, session.bookingType, groupBookingLabel);

    // Initialize booking when component mounts or bookingType/clientPortalGuid changes
    useEffect(() => {
        if (!clientPortalGuid) return;

        dispatch(
            initializeBooking({
                clientPortalGuid,
                bookingType,
            })
        );
    }, [clientPortalGuid, bookingType, dispatch]);

    useEffect(() => {
        if (session.cancelModalOpen) {
            void confirm();
        }
    }, [session.cancelModalOpen, confirm]);

    useEffect(() => {
        if (clientPortalGuid) {
            dispatch(setClientPortalGuid(clientPortalGuid));
        }
    }, [dispatch, clientPortalGuid]);

    return (
        <Content dataTestIdSuffix="appointment_booking">
            <Box className={styles.wrapper}>
                <Typography variant="heading3">{headingText}</Typography>
                <Box
                    className={styles.contentContainer}
                    sx={(theme) => ({
                        [theme.breakpoints.down("sm")]: {
                            flexDirection: "column",
                            width: "100%",
                            padding: 0,
                        },
                    })}
                >
                    <BookingFlow />
                    {shouldShowSummary && <Summary />}
                </Box>
            </Box>
        </Content>
    );
}
